/**
 * Enhanced Error Handling System for Cedar Heights Music Academy Admin System
 *
 * This module provides comprehensive error handling capabilities including:
 * - Specific error types for different failure scenarios
 * - User-friendly error messages with recovery suggestions
 * - Automatic retry logic with exponential backoff
 * - Error logging and monitoring integration
 * - Fallback UI states for degraded functionality
 * - Error boundary integration with crash reporting
 * - Circuit breaker patterns for network resilience
 * - Production monitoring service integration
 * - Structured error logging with correlation IDs
 */

import { ApiError } from './apiTypes';

/**
 * Enhanced error severity levels
 */
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

/**
 * Error categories for better classification
 */
export type ErrorCategory = 
  | 'network'
  | 'authentication'
  | 'authorization'
  | 'validation'
  | 'business_logic'
  | 'server'
  | 'client'
  | 'external_service'
  | 'timeout'
  | 'rate_limit'
  | 'unknown';

/**
 * Recovery action types
 */
export type RecoveryAction =
  | 'retry'
  | 'refresh_page'
  | 'login_again'
  | 'contact_support'
  | 'try_later'
  | 'check_connection'
  | 'validate_input'
  | 'circuit_breaker_open'
  | 'fallback_mode'
  | 'none';

/**
 * Circuit breaker states
 */
export type CircuitBreakerState = 'closed' | 'open' | 'half_open';

/**
 * Circuit breaker configuration
 */
export interface CircuitBreakerConfig {
  failureThreshold: number;
  recoveryTimeout: number;
  monitoringWindow: number;
  minimumThroughput: number;
}

/**
 * Error monitoring configuration
 */
export interface ErrorMonitoringConfig {
  enabled: boolean;
  sampleRate: number;
  endpoint?: string;
  apiKey?: string;
  environment: string;
  release?: string;
}

/**
 * Structured log entry interface
 */
export interface StructuredLogEntry {
  timestamp: string;
  level: 'error' | 'warn' | 'info' | 'debug';
  message: string;
  correlationId: string;
  userId?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
  component?: string;
  operation?: string;
  duration?: number;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code: string;
    status: number;
    category: ErrorCategory;
    severity: ErrorSeverity;
  };
  context?: Record<string, unknown>;
  tags?: string[];
  fingerprint?: string;
}

/**
 * Enhanced API Error class with comprehensive error handling
 */
export class EnhancedApiError extends ApiError {
  public category: ErrorCategory;
  public severity: ErrorSeverity;
  public retryable: boolean;
  public userMessage: string;
  public recoveryActions: RecoveryAction[];
  public context?: Record<string, unknown>;
  public timestamp: Date;
  public correlationId: string;

  constructor(
    message: string,
    status: number,
    code: string,
    options: {
      category?: ErrorCategory;
      severity?: ErrorSeverity;
      retryable?: boolean;
      userMessage?: string;
      recoveryActions?: RecoveryAction[];
      context?: Record<string, unknown>;
      correlationId?: string;
    } = {}
  ) {
    super(message, status, code, options.context);
    this.name = 'EnhancedApiError';
    this.category = options.category || this.categorizeError(status, code);
    this.severity = options.severity || this.determineSeverity(status, code);
    this.retryable = options.retryable ?? this.determineRetryability(status, code);
    this.userMessage = options.userMessage || this.generateUserMessage(status, code, message);
    this.recoveryActions = options.recoveryActions || this.determineRecoveryActions(status, code);
    this.context = options.context;
    this.timestamp = new Date();
    this.correlationId = options.correlationId || this.generateCorrelationId();
  }

  /**
   * Categorize error based on status and code
   */
  private categorizeError(status: number, code: string): ErrorCategory {
    if (code === 'NETWORK_ERROR' || status === 0) return 'network';
    if (code === 'TIMEOUT_ERROR') return 'timeout';
    if (status === 401 || code === 'UNAUTHORIZED') return 'authentication';
    if (status === 403 || code === 'FORBIDDEN') return 'authorization';
    if (status === 422 || code === 'VALIDATION_ERROR') return 'validation';
    if (status === 429 || code === 'RATE_LIMITED') return 'rate_limit';
    if (status >= 500) return 'server';
    if (status >= 400 && status < 500) return 'client';
    if (code.includes('EXTERNAL')) return 'external_service';
    return 'unknown';
  }

  /**
   * Determine error severity
   */
  private determineSeverity(status: number, code: string): ErrorSeverity {
    if (status === 401 || status === 403) return 'high';
    if (status >= 500) return 'critical';
    if (code === 'NETWORK_ERROR' || code === 'TIMEOUT_ERROR') return 'medium';
    if (status === 422) return 'low';
    if (status === 429) return 'medium';
    return 'low';
  }

  /**
   * Determine if error is retryable
   */
  private determineRetryability(status: number, code: string): boolean {
    // Network and timeout errors are retryable
    if (code === 'NETWORK_ERROR' || code === 'TIMEOUT_ERROR') return true;
    
    // Server errors (5xx) are generally retryable
    if (status >= 500) return true;
    
    // Rate limiting is retryable with backoff
    if (status === 429) return true;
    
    // Authentication errors might be retryable after token refresh
    if (status === 401) return true;
    
    // Client errors (4xx except 401, 429) are generally not retryable
    if (status >= 400 && status < 500 && status !== 401 && status !== 429) return false;
    
    return false;
  }

  /**
   * Generate user-friendly error message
   */
  private generateUserMessage(status: number, code: string, originalMessage: string): string {
    // Check for CORS-specific errors first
    if (originalMessage.includes('CORS') || originalMessage.includes('Access-Control-Allow-Origin')) {
      return 'Server configuration issue: The server is not properly configured to handle requests from this application. This is a technical issue that requires server-side configuration changes. Please contact the development team.';
    }
    
    // Check for fetch failures that are likely CORS
    if (originalMessage.includes('Failed to fetch') && status === 0) {
      return 'Unable to connect to server: The server may be blocking requests from this development environment due to security restrictions (CORS policy). Please contact the backend team to resolve this configuration issue.';
    }

    switch (code) {
      case 'NETWORK_ERROR':
        // Enhanced network error message
        return 'Connection failed: Unable to reach the server. This could be due to network connectivity issues, server downtime, or security restrictions. Please check your internet connection and try again.';
      
      case 'TIMEOUT_ERROR':
        return 'Request timeout: The server took too long to respond. This might be due to server overload or network issues. Please try again in a moment.';
      
      case 'UNAUTHORIZED':
        return 'Authentication required: Your session has expired or you don\'t have permission to access this resource. Please log in again to continue.';
      
      case 'FORBIDDEN':
        return 'Access denied: You don\'t have permission to perform this action. If you believe this is an error, please contact your administrator.';
      
      case 'VALIDATION_ERROR':
        return 'Input validation failed: Please check your input and try again. Make sure all required fields are filled out correctly.';
      
      case 'RATE_LIMITED':
        return 'Too many requests: Please wait a moment before trying again. The server is temporarily limiting requests to prevent overload.';
      
      case 'SERVER_ERROR':
        return 'Server error: The server encountered an internal error and could not complete your request. This is a temporary issue. Please try again in a few minutes.';
      
      default:
        if (status >= 500) {
          return 'Server error: The server encountered an internal error and could not complete your request. This is a temporary issue. Please try again in a few minutes. If the problem persists, contact support.';
        }
        if (status === 404) {
          return 'Resource not found: The requested information could not be located. It may have been moved, deleted, or you may not have access to it.';
        }
        if (status >= 400) {
          return originalMessage || 'Request failed: An error occurred while processing your request. Please check your input and try again.';
        }
        return originalMessage || 'An unexpected error occurred. Please try again. If the problem persists, contact support.';
    }
  }

  /**
   * Determine recovery actions
   */
  private determineRecoveryActions(status: number, code: string): RecoveryAction[] {
    switch (code) {
      case 'NETWORK_ERROR':
        return ['check_connection', 'retry'];
      
      case 'TIMEOUT_ERROR':
        return ['retry', 'try_later'];
      
      case 'UNAUTHORIZED':
        return ['login_again'];
      
      case 'FORBIDDEN':
        return ['contact_support'];
      
      case 'VALIDATION_ERROR':
        return ['validate_input'];
      
      case 'RATE_LIMITED':
        return ['try_later', 'retry'];
      
      case 'SERVER_ERROR':
        return ['retry', 'try_later', 'contact_support'];
      
      default:
        if (status >= 500) {
          return ['retry', 'try_later', 'contact_support'];
        }
        if (status === 401) {
          return ['login_again'];
        }
        if (status === 403) {
          return ['contact_support'];
        }
        if (status >= 400) {
          return ['validate_input', 'contact_support'];
        }
        return ['retry', 'refresh_page'];
    }
  }

  /**
   * Generate correlation ID for error tracking
   */
  private generateCorrelationId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Convert to JSON for logging
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      userMessage: this.userMessage,
      status: this.status,
      code: this.code,
      category: this.category,
      severity: this.severity,
      retryable: this.retryable,
      recoveryActions: this.recoveryActions,
      context: this.context,
      timestamp: this.timestamp.toISOString(),
      correlationId: this.correlationId,
      stack: this.stack,
    };
  }
}

/**
 * Retry configuration interface
 */
export interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitterMax: number;
  retryableErrorCodes: string[];
  retryableStatusCodes: number[];
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  jitterMax: 1000, // 1 second
  retryableErrorCodes: [
    'NETWORK_ERROR',
    'TIMEOUT_ERROR',
    'SERVER_ERROR',
    'RATE_LIMITED',
  ],
  retryableStatusCodes: [429, 500, 502, 503, 504],
};

/**
 * Default circuit breaker configuration
 */
export const DEFAULT_CIRCUIT_BREAKER_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 5, // Open circuit after 5 failures
  recoveryTimeout: 60000, // 1 minute recovery timeout
  monitoringWindow: 300000, // 5 minute monitoring window
  minimumThroughput: 10, // Minimum requests before circuit breaker activates
};

/**
 * Default error monitoring configuration
 */
export const DEFAULT_ERROR_MONITORING_CONFIG: ErrorMonitoringConfig = {
  enabled: typeof process !== 'undefined' && process.env?.NODE_ENV === 'production',
  sampleRate: 1.0, // 100% sampling in production
  environment: typeof process !== 'undefined' ? (process.env?.NODE_ENV || 'development') : 'development',
  release: typeof process !== 'undefined' ? process.env?.VITE_APP_VERSION : undefined,
};

/**
 * Circuit breaker implementation for network resilience
 */
class CircuitBreaker {
  private state: CircuitBreakerState = 'closed';
  private failureCount = 0;
  private lastFailureTime = 0;
  private successCount = 0;
  private requestCount = 0;
  private config: CircuitBreakerConfig;

  constructor(config: CircuitBreakerConfig = DEFAULT_CIRCUIT_BREAKER_CONFIG) {
    this.config = config;
  }

  /**
   * Execute operation with circuit breaker protection
   */
  async execute<T>(operation: () => Promise<T>, fallback?: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime > this.config.recoveryTimeout) {
        this.state = 'half_open';
        this.successCount = 0;
      } else {
        if (fallback) {
          return fallback();
        }
        throw new EnhancedApiError(
          'Circuit breaker is open - service temporarily unavailable',
          503,
          'CIRCUIT_BREAKER_OPEN',
          {
            category: 'network',
            severity: 'high',
            retryable: false,
            userMessage: 'Service is temporarily unavailable. Please try again later.',
            recoveryActions: ['circuit_breaker_open', 'try_later'],
          }
        );
      }
    }

    this.requestCount++;

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  /**
   * Handle successful operation
   */
  private onSuccess(): void {
    this.failureCount = 0;
    
    if (this.state === 'half_open') {
      this.successCount++;
      if (this.successCount >= 3) { // Require 3 successes to close circuit
        this.state = 'closed';
        this.successCount = 0;
      }
    }
  }

  /**
   * Handle failed operation
   */
  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.requestCount >= this.config.minimumThroughput &&
        this.failureCount >= this.config.failureThreshold) {
      this.state = 'open';
    }
  }

  /**
   * Get current circuit breaker state
   */
  getState(): CircuitBreakerState {
    return this.state;
  }

  /**
   * Get circuit breaker metrics
   */
  getMetrics(): {
    state: CircuitBreakerState;
    failureCount: number;
    successCount: number;
    requestCount: number;
    lastFailureTime: number;
  } {
    return {
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      requestCount: this.requestCount,
      lastFailureTime: this.lastFailureTime,
    };
  }

  /**
   * Reset circuit breaker to closed state
   */
  reset(): void {
    this.state = 'closed';
    this.failureCount = 0;
    this.successCount = 0;
    this.requestCount = 0;
    this.lastFailureTime = 0;
  }
}

/**
 * Enhanced error handler with comprehensive error processing
 */
export class EnhancedErrorHandler {
  private static instance: EnhancedErrorHandler;
  private errorListeners: Array<(error: EnhancedApiError) => void> = [];
  private retryConfig: RetryConfig;
  private circuitBreakers: Map<string, CircuitBreaker> = new Map();
  private monitoringConfig: ErrorMonitoringConfig;
  private sessionId: string;

  constructor(
    retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG,
    monitoringConfig: ErrorMonitoringConfig = DEFAULT_ERROR_MONITORING_CONFIG
  ) {
    this.retryConfig = retryConfig;
    this.monitoringConfig = monitoringConfig;
    this.sessionId = this.generateSessionId();
  }

  /**
   * Get singleton instance
   */
  static getInstance(
    retryConfig?: RetryConfig,
    monitoringConfig?: ErrorMonitoringConfig
  ): EnhancedErrorHandler {
    if (!EnhancedErrorHandler.instance) {
      EnhancedErrorHandler.instance = new EnhancedErrorHandler(retryConfig, monitoringConfig);
    }
    return EnhancedErrorHandler.instance;
  }

  /**
   * Generate session ID for tracking user sessions
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get or create circuit breaker for endpoint
   */
  private getCircuitBreaker(endpoint: string): CircuitBreaker {
    if (!this.circuitBreakers.has(endpoint)) {
      this.circuitBreakers.set(endpoint, new CircuitBreaker());
    }
    return this.circuitBreakers.get(endpoint)!;
  }

  /**
   * Execute operation with circuit breaker protection
   */
  async withCircuitBreaker<T>(
    operation: () => Promise<T>,
    endpoint: string,
    fallback?: () => Promise<T>
  ): Promise<T> {
    const circuitBreaker = this.getCircuitBreaker(endpoint);
    return circuitBreaker.execute(operation, fallback);
  }

  /**
   * Handle and transform any error to EnhancedApiError
   */
  handleError(error: unknown, context?: Record<string, unknown>): EnhancedApiError {
    let enhancedError: EnhancedApiError;

    if (error instanceof EnhancedApiError) {
      // Already enhanced, just add context if provided
      if (context) {
        enhancedError = new EnhancedApiError(
          error.message,
          error.status,
          error.code,
          {
            category: error.category,
            severity: error.severity,
            retryable: error.retryable,
            userMessage: error.userMessage,
            recoveryActions: error.recoveryActions,
            context: { ...error.context, ...context },
            correlationId: error.correlationId,
          }
        );
      } else {
        enhancedError = error;
      }
    } else if (error instanceof ApiError) {
      // Transform existing ApiError
      enhancedError = new EnhancedApiError(
        error.message,
        error.status,
        error.code,
        { context: { ...error.details, ...context } }
      );
    } else if (error instanceof Error) {
      // Transform generic Error
      const status = this.getStatusFromError(error);
      const code = this.getCodeFromError(error);
      
      enhancedError = new EnhancedApiError(
        error.message,
        status,
        code,
        { context }
      );
    } else {
      // Handle unknown error types
      const message = typeof error === 'string' ? error : 'An unknown error occurred';
      enhancedError = new EnhancedApiError(
        message,
        500,
        'UNKNOWN_ERROR',
        { 
          category: 'unknown',
          severity: 'medium',
          context: { originalError: error, ...context }
        }
      );
    }

    // Notify error listeners
    this.notifyErrorListeners(enhancedError);

    // Log error for monitoring
    this.logError(enhancedError);

    return enhancedError;
  }

  /**
   * Execute operation with retry logic
   */
  async withRetry<T>(
    operation: () => Promise<T>,
    customConfig?: Partial<RetryConfig>,
    context?: Record<string, unknown>
  ): Promise<T> {
    const config = { ...this.retryConfig, ...customConfig };
    let lastError: EnhancedApiError;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = this.handleError(error, { ...context, attempt });

        // Don't retry if it's the last attempt or error is not retryable
        if (attempt === config.maxRetries || !this.isRetryable(lastError, config)) {
          throw lastError;
        }

        // Calculate delay with exponential backoff and jitter
        const delay = Math.min(
          config.baseDelay * Math.pow(config.backoffMultiplier, attempt),
          config.maxDelay
        );
        const jitteredDelay = delay + Math.random() * config.jitterMax;

        await this.delay(jitteredDelay);
      }
    }

    throw lastError!;
  }

  /**
   * Add error listener for monitoring/logging
   */
  addErrorListener(listener: (error: EnhancedApiError) => void): void {
    this.errorListeners.push(listener);
  }

  /**
   * Remove error listener
   */
  removeErrorListener(listener: (error: EnhancedApiError) => void): void {
    const index = this.errorListeners.indexOf(listener);
    if (index > -1) {
      this.errorListeners.splice(index, 1);
    }
  }

  /**
   * Check if error is retryable based on configuration
   */
  private isRetryable(error: EnhancedApiError, config: RetryConfig): boolean {
    return (
      error.retryable &&
      (config.retryableErrorCodes.includes(error.code) ||
       config.retryableStatusCodes.includes(error.status))
    );
  }

  /**
   * Get status code from generic error
   */
  private getStatusFromError(error: Error): number {
    if (error.name === 'NetworkError') return 0;
    if (error.name === 'TimeoutError') return 0;
    if (error.message.includes('timeout')) return 0;
    if (error.message.includes('network')) return 0;
    return 500;
  }

  /**
   * Get error code from generic error
   */
  private getCodeFromError(error: Error): string {
    if (error.name === 'NetworkError') return 'NETWORK_ERROR';
    if (error.name === 'TimeoutError') return 'TIMEOUT_ERROR';
    if (error.message.includes('timeout')) return 'TIMEOUT_ERROR';
    if (error.message.includes('network')) return 'NETWORK_ERROR';
    if (error.message.includes('validation')) return 'VALIDATION_ERROR';
    if (error.message.includes('CORS') || error.message.includes('Access-Control-Allow-Origin')) return 'CORS_ERROR';
    if (error.message.includes('Failed to fetch')) return 'FETCH_ERROR';
    return 'GENERIC_ERROR';
  }

  /**
   * Notify all error listeners
   */
  private notifyErrorListeners(error: EnhancedApiError): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (listenerError) {
        console.error('Error in error listener:', listenerError);
      }
    });
  }

  /**
   * Log error for monitoring and debugging with structured logging
   */
  private logError(error: EnhancedApiError): void {
    const logEntry = this.createStructuredLogEntry(error);
    const logLevel = this.getLogLevel(error.severity);

    // Console logging with structured format
    console[logLevel](`[${error.correlationId}] ${error.category.toUpperCase()} ERROR:`, logEntry);

    // Send to monitoring service if enabled
    if (this.monitoringConfig.enabled) {
      this.sendToMonitoringService(error, logEntry);
    }

    // Store error metrics for circuit breaker analysis
    this.updateErrorMetrics(error);
  }

  /**
   * Create structured log entry
   */
  private createStructuredLogEntry(error: EnhancedApiError): StructuredLogEntry {
    const userId = this.getCurrentUserId();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
    const url = typeof window !== 'undefined' ? window.location.href : 'unknown';

    return {
      timestamp: new Date().toISOString(),
      level: this.getLogLevel(error.severity),
      message: error.message,
      correlationId: error.correlationId,
      userId,
      sessionId: this.sessionId,
      userAgent,
      url,
      component: error.context?.service as string || 'unknown',
      operation: error.context?.operation as string || 'unknown',
      duration: error.context?.duration as number,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        code: error.code,
        status: error.status,
        category: error.category,
        severity: error.severity,
      },
      context: error.context,
      tags: this.generateErrorTags(error),
      fingerprint: this.generateErrorFingerprint(error),
    };
  }

  /**
   * Generate error tags for categorization
   */
  private generateErrorTags(error: EnhancedApiError): string[] {
    const tags = [
      `category:${error.category}`,
      `severity:${error.severity}`,
      `status:${error.status}`,
      `retryable:${error.retryable}`,
    ];

    if (error.context?.service) {
      tags.push(`service:${error.context.service}`);
    }

    if (error.context?.operation) {
      tags.push(`operation:${error.context.operation}`);
    }

    return tags;
  }

  /**
   * Generate error fingerprint for deduplication
   */
  private generateErrorFingerprint(error: EnhancedApiError): string {
    const components = [
      error.code,
      error.status.toString(),
      error.category,
      error.context?.service || 'unknown',
      error.context?.operation || 'unknown',
    ];

    return components.join(':');
  }

  /**
   * Get current user ID from localStorage or auth store
   */
  private getCurrentUserId(): string | undefined {
    try {
      // Try to get user ID from localStorage token
      const token = localStorage.getItem('auth_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || payload.user_id;
      }
    } catch {
      // Ignore token parsing errors
    }
    return undefined;
  }

  /**
   * Update error metrics for analysis
   */
  private updateErrorMetrics(error: EnhancedApiError): void {
    // This could be enhanced to store metrics in localStorage or IndexedDB
    // for offline analysis and circuit breaker decision making
    const metricsKey = `error_metrics_${error.context?.service || 'unknown'}`;
    const existingMetrics = JSON.parse(localStorage.getItem(metricsKey) || '{}');
    
    const today = new Date().toISOString().split('T')[0];
    if (!existingMetrics[today]) {
      existingMetrics[today] = { count: 0, categories: {} };
    }
    
    existingMetrics[today].count++;
    existingMetrics[today].categories[error.category] =
      (existingMetrics[today].categories[error.category] || 0) + 1;
    
    localStorage.setItem(metricsKey, JSON.stringify(existingMetrics));
  }

  /**
   * Get appropriate log level for error severity
   */
  private getLogLevel(severity: ErrorSeverity): 'error' | 'warn' | 'info' {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warn';
      case 'low':
      default:
        return 'info';
    }
  }

  /**
   * Send error to monitoring service with enhanced production integration
   */
  private sendToMonitoringService(error: EnhancedApiError, logEntry?: StructuredLogEntry): void {
    if (!this.monitoringConfig.enabled) {
      return;
    }

    // Sample rate check
    if (Math.random() > this.monitoringConfig.sampleRate) {
      return;
    }

    try {
      // In a real implementation, this would send to services like:
      // - Sentry: Sentry.captureException(error, { extra: logEntry })
      // - LogRocket: LogRocket.captureException(error)
      // - Bugsnag: Bugsnag.notify(error, { metaData: logEntry })
      // - DataDog: DD_LOGS.logger.error(error.message, logEntry)
      
      if (this.monitoringConfig.endpoint && this.monitoringConfig.apiKey) {
        // Send to custom monitoring endpoint
        this.sendToCustomMonitoringEndpoint(error, logEntry);
      } else {
        // Fallback to console logging with monitoring format
        console.info('[MONITORING]', {
          service: 'cedar-heights-music-admin',
          environment: this.monitoringConfig.environment,
          release: this.monitoringConfig.release,
          error: logEntry || error.toJSON(),
          timestamp: new Date().toISOString(),
        });
      }
    } catch (monitoringError) {
      // Don't let monitoring errors break the application
      console.warn('Failed to send error to monitoring service:', monitoringError);
    }
  }

  /**
   * Send error to custom monitoring endpoint
   */
  private async sendToCustomMonitoringEndpoint(error: EnhancedApiError, logEntry?: StructuredLogEntry): Promise<void> {
    if (!this.monitoringConfig.endpoint || !this.monitoringConfig.apiKey) {
      return;
    }

    try {
      const payload = {
        service: 'cedar-heights-music-admin',
        environment: this.monitoringConfig.environment,
        release: this.monitoringConfig.release,
        error: logEntry || error.toJSON(),
        timestamp: new Date().toISOString(),
      };

      await fetch(this.monitoringConfig.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.monitoringConfig.apiKey}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (fetchError) {
      console.warn('Failed to send error to custom monitoring endpoint:', fetchError);
    }
  }

  /**
   * Delay utility for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get circuit breaker metrics for all endpoints
   */
  getCircuitBreakerMetrics(): Record<string, ReturnType<CircuitBreaker['getMetrics']>> {
    const metrics: Record<string, ReturnType<CircuitBreaker['getMetrics']>> = {};
    this.circuitBreakers.forEach((breaker, endpoint) => {
      metrics[endpoint] = breaker.getMetrics();
    });
    return metrics;
  }

  /**
   * Reset circuit breaker for specific endpoint
   */
  resetCircuitBreaker(endpoint: string): void {
    const breaker = this.circuitBreakers.get(endpoint);
    if (breaker) {
      breaker.reset();
    }
  }

  /**
   * Reset all circuit breakers
   */
  resetAllCircuitBreakers(): void {
    this.circuitBreakers.forEach(breaker => breaker.reset());
  }

  /**
   * Configure monitoring settings
   */
  configureMonitoring(config: Partial<ErrorMonitoringConfig>): void {
    this.monitoringConfig = { ...this.monitoringConfig, ...config };
  }

  /**
   * Get error metrics from localStorage
   */
  getErrorMetrics(service?: string): Record<string, unknown> {
    const metricsKey = service ? `error_metrics_${service}` : 'error_metrics_unknown';
    return JSON.parse(localStorage.getItem(metricsKey) || '{}');
  }

  /**
   * Clear error metrics
   */
  clearErrorMetrics(service?: string): void {
    const metricsKey = service ? `error_metrics_${service}` : 'error_metrics_unknown';
    localStorage.removeItem(metricsKey);
  }

  /**
   * Get health status of error handling system
   */
  getHealthStatus(): {
    circuitBreakers: Record<string, CircuitBreakerState>;
    errorMetrics: Record<string, unknown>;
    monitoringEnabled: boolean;
    sessionId: string;
  } {
    const circuitBreakers: Record<string, CircuitBreakerState> = {};
    this.circuitBreakers.forEach((breaker, endpoint) => {
      circuitBreakers[endpoint] = breaker.getState();
    });

    return {
      circuitBreakers,
      errorMetrics: this.getErrorMetrics(),
      monitoringEnabled: this.monitoringConfig.enabled,
      sessionId: this.sessionId,
    };
  }
}

/**
 * Global error handler instance
 */
export const errorHandler = EnhancedErrorHandler.getInstance();

/**
 * Utility functions for error handling
 */
export const errorUtils = {
  /**
   * Check if error is an enhanced API error
   */
  isEnhancedApiError: (error: unknown): error is EnhancedApiError => {
    return error instanceof EnhancedApiError;
  },

  /**
   * Extract user-friendly message from any error
   */
  getUserMessage: (error: unknown): string => {
    if (errorUtils.isEnhancedApiError(error)) {
      return error.userMessage;
    }
    if (error instanceof ApiError) {
      return error.message;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },

  /**
   * Get recovery actions from error
   */
  getRecoveryActions: (error: unknown): RecoveryAction[] => {
    if (errorUtils.isEnhancedApiError(error)) {
      return error.recoveryActions;
    }
    return ['retry', 'contact_support'];
  },

  /**
   * Check if error is retryable
   */
  isRetryable: (error: unknown): boolean => {
    if (errorUtils.isEnhancedApiError(error)) {
      return error.retryable;
    }
    if (error instanceof ApiError) {
      return error.status >= 500 || error.status === 429 || error.code === 'NETWORK_ERROR';
    }
    return false;
  },

  /**
   * Get error severity
   */
  getSeverity: (error: unknown): ErrorSeverity => {
    if (errorUtils.isEnhancedApiError(error)) {
      return error.severity;
    }
    return 'medium';
  },

  /**
   * Create enhanced error from basic error info
   */
  createError: (
    message: string,
    status: number = 500,
    code: string = 'GENERIC_ERROR',
    options?: Partial<{
      category: ErrorCategory;
      severity: ErrorSeverity;
      retryable: boolean;
      userMessage: string;
      recoveryActions: RecoveryAction[];
      context: Record<string, unknown>;
    }>
  ): EnhancedApiError => {
    return new EnhancedApiError(message, status, code, options);
  },
};

export default errorHandler;